<template>
  <div v-if="show" class="modal fade show d-block" tabindex="-1" role="dialog" aria-labelledby="editUserModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header bg-primary text-white">
          <h5 class="modal-title text-white" id="editUserModalLabel">
            <i class="mdi mdi-account-edit me-2"></i>Editing User Details
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <!-- Personal Information Section -->
          <div class="mb-4">
            <h6 class="text-primary border-bottom pb-2 mb-3">
              <i class="mdi mdi-account-circle me-2"></i>Personal Information
            </h6>
            <div class="row">
              <div class="col-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Full Names <span class="text-danger">*</span></label>
                  <input :readonly="!getRight('EDIT ALL USER DETAILS')" v-model="formData.names" type="text" class="form-control" placeholder="Enter The Full Names">
                </div>
              </div>

              <div class="col-6 mb-3">
                <div class="form-group">
                  <label class="form-label">ID Number <span class="text-danger">*</span></label>
                  <input :readonly="!getRight('EDIT ALL USER DETAILS')" v-model="formData.idNo" type="text" class="form-control" placeholder="Enter ID No.">
                </div>
              </div>

              <div class="col-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Gender</label>
                  <select :disabled="!getRight('EDIT ALL USER DETAILS')" class="form-select" v-model="formData.gender">
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div class="col-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                  <input :readonly="!getRight('EDIT ALL USER DETAILS')" v-model="formData.phoneNumber" type="text" class="form-control" placeholder="Enter Phone Number">
                </div>
              </div>

              <div class="col-12 mb-3">
                <div class="form-group">
                  <label class="form-label">Email Address</label>
                  <input :readonly="!getRight('EDIT ALL USER DETAILS')" v-model="formData.email" type="email" class="form-control" placeholder="email@email.com">
                </div>
              </div>
            </div>
          </div>

          <!-- System & Role Assignment Section -->
          <div class="mb-4">
            <h6 class="text-primary border-bottom pb-2 mb-3">
              <i class="mdi mdi-shield-account me-2"></i>System & Role Assignment
            </h6>
            <div class="row">
              <!-- System Selection -->
              <div class="col-md-4 mb-3">
                <div class="form-group">
                  <label class="form-label">System <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="formData.systemID" @change="handleSystemChange">
                    <option value="">Select System</option>
                    <option v-for="system in systems" :key="system.id" :value="system.id">
                      {{ system.name }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- User Role/Designation -->
              <div class="col-md-4 mb-3">
                <div class="form-group">
                  <label class="form-label">User Role <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="formData.category" :disabled="!formData.systemID">
                    <option value="">Select Role</option>
                    <option v-for="(item, index) in filteredCategories" :key="index" :value="item.category">
                      {{ item.category }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Facility Access Type (Hospital Only) -->
              <div v-if="isHospitalSystem" class="col-md-4 mb-3">
                <div class="form-group">
                  <label class="form-label">Facility Access <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="formData.facilityAccessType" @change="handleFacilityAccessTypeChange">
                    <option value="single">Single Facility</option>
                    <option value="multiple">Multiple Facilities</option>
                    <option value="all">All Facilities</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Facility Selection (Hospital Only) -->
            <div v-if="isHospitalSystem" class="row">
              <!-- Primary Facility -->
              <div v-if="formData.facilityAccessType !== 'all'" class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">
                    {{ formData.facilityAccessType === 'single' ? 'Facility' : 'Primary Facility' }}
                    <span class="text-danger">*</span>
                  </label>
                  <select class="form-select" v-model="formData.facilityID">
                    <option value="">Select Facility</option>
                    <option v-for="facility in facilities" :key="facility.facilityID" :value="facility.facilityID">
                      {{ facility.facilityName }} ({{ facility.facilityCode }})
                    </option>
                  </select>
                </div>
              </div>

              <!-- Additional Facilities (Multiple Only) -->
              <div v-if="formData.facilityAccessType === 'multiple'" class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Additional Facilities</label>
                  <div class="border rounded p-2 bg-light" style="max-height: 150px; overflow-y: auto;">
                    <div v-for="facility in facilities" :key="facility.facilityID" class="form-check">
                      <input
                          type="checkbox"
                          class="form-check-input"
                          :id="'edit_facility_' + facility.facilityID"
                          :value="facility.facilityID"
                          v-model="formData.additionalFacilities"
                          :disabled="formData.facilityID === facility.facilityID">
                      <label class="form-check-label" :for="'edit_facility_' + facility.facilityID">
                        {{ facility.facilityName }}
                        <span v-if="formData.facilityID === facility.facilityID" class="badge bg-primary ms-1">Primary</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- All Facilities Info -->
              <div v-if="formData.facilityAccessType === 'all'" class="col-12 mb-3">
                <div class="alert alert-info mb-0">
                  <i class="mdi mdi-information me-2"></i>
                  This user will have access to <strong>all {{ facilities.length }} facilities</strong> in the system.
                </div>
              </div>
            </div>
          </div>

          <!-- Location Assignment Section -->
          <div class="mb-4">
            <h6 class="text-primary border-bottom pb-2 mb-3">
              <i class="mdi mdi-map-marker me-2"></i>Location Assignment
            </h6>
            <div class="row">
              <div class="col-md-4 mb-3">
                <div class="form-group">
                  <label class="form-label">SubCounty <span class="text-danger">*</span></label>
                  <select class="form-select" @change="handleSubCountyChange" v-model="formData.subCountyID">
                    <option value="">Select SubCounty</option>
                    <option v-for="(item, index) in subCounties" :key="index" :value="item.subCountyID">
                      {{ item.subCountyName }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="col-md-4 mb-3">
                <div class="form-group">
                  <label class="form-label">Ward <span class="text-danger">*</span></label>
                  <select class="form-select" v-model="formData.wardID" :disabled="!formData.subCountyID">
                    <option value="">Select Ward</option>
                    <option v-for="(item, index) in wards" :key="index" :value="item.wardID">
                      {{ item.wardName }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="col-md-4 mb-3">
                <div class="form-group">
                  <label class="form-label">Zone(s) <span v-if="!isHospitalSystem" class="text-danger">*</span></label>
                  <select class="form-select" v-model="formData.zone" multiple :disabled="!formData.wardID" style="min-height: 80px;">
                    <option v-for="(item, index) in zones" :key="index" :value="item.zone">
                      {{ item.zone }}
                    </option>
                  </select>
                  <small class="text-muted">Hold CTRL to select multiple</small>
                </div>
              </div>
            </div>
          </div>

          <!-- Security Settings Section -->
          <div class="mb-3">
            <h6 class="text-primary border-bottom pb-2 mb-3">
              <i class="mdi mdi-security me-2"></i>Security Settings
            </h6>
            <div class="form-check form-switch">
              <input type="checkbox" class="form-check-input" id="editRequireOTP" v-model="formData.requireOTP">
              <label class="form-check-label" for="editRequireOTP">
                <strong>Require OTP for login</strong>
                <br>
                <small class="text-muted">When enabled, user will receive an OTP on their phone for login</small>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            <i class="mdi mdi-close me-1"></i>Cancel
          </button>
          <button @click="handleSave" class="btn btn-primary" :disabled="!isFormValid">
            <i class="mdi mdi-content-save me-1"></i>Save Changes
          </button>
        </div>
      </div>
    </div>
  </div>
  <div v-if="show" class="modal-backdrop fade show"></div>
</template>

<script>
import {getRight} from "@/api";

export default {
  name: "EditUserModal",
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedUser: {
      type: Object,
      default: () => ({})
    },
    categories: {
      type: Array,
      default: () => []
    },
    subCounties: {
      type: Array,
      default: () => []
    },
    wards: {
      type: Array,
      default: () => []
    },
    zones: {
      type: Array,
      default: () => []
    },
    // New props for multi-system support
    systems: {
      type: Array,
      default: () => []
    },
    facilities: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      formData: {
        names: '',
        idNo: '',
        gender: '',
        category: '',
        phoneNumber: '',
        email: '',
        subCountyID: '',
        subCountyName: '',
        wardID: '',
        wardName: '',
        zone: [],
        requireOTP: false,
        id: null,
        // New fields
        systemID: '',
        facilityID: '',
        facilityAccess: '',
        facilityAccessType: 'single',
        additionalFacilities: []
      }
    }
  },
  computed: {
    isHospitalSystem() {
      if (!this.formData.systemID) return false;
      const system = this.systems.find(s => s.id == this.formData.systemID);
      return system && system.code === 'HOSPITAL';
    },
    filteredCategories() {
      if (!this.formData.systemID) return this.categories;
      return this.categories.filter(cat => {
        return cat.systemID == this.formData.systemID || !cat.systemID;
      });
    },
    isFormValid() {
      let valid = this.formData.names &&
          this.formData.phoneNumber &&
          this.formData.idNo &&
          this.formData.category &&
          this.formData.subCountyID &&
          this.formData.wardID;

      // Zone required for non-hospital users
      if (!this.isHospitalSystem) {
        valid = valid && this.formData.zone.length > 0;
      }

      // Facility required for hospital users (unless "all" access)
      if (this.isHospitalSystem && this.formData.facilityAccessType !== 'all') {
        valid = valid && this.formData.facilityID;
      }

      return valid;
    }
  },
  watch: {
    selectedUser: {
      handler(newUser) {
        if (newUser && Object.keys(newUser).length > 0) {
          // Parse facility access type
          let facilityAccessType = 'single';
          let additionalFacilities = [];

          if (newUser.facilityAccess === 'ALL') {
            facilityAccessType = 'all';
          } else if (newUser.facilityAccess) {
            try {
              const facilities = JSON.parse(newUser.facilityAccess);
              if (Array.isArray(facilities) && facilities.length > 1) {
                facilityAccessType = 'multiple';
                additionalFacilities = facilities.filter(f => f !== newUser.facilityID);
              }
            } catch (e) {
              // Not JSON, keep as single
            }
          }

          this.formData = {
            names: newUser.names || '',
            idNo: newUser.idNo || '',
            gender: newUser.gender || '',
            category: newUser.category || '',
            phoneNumber: newUser.phoneNumber || '',
            email: newUser.email || '',
            subCountyID: newUser.subCountyID || '',
            subCountyName: newUser.subCountyName || '',
            wardID: newUser.wardID || '',
            wardName: newUser.wardName || '',
            zone: this.parseZone(newUser.zone),
            requireOTP: newUser.requireOTP === 'true' || newUser.requireOTP === true,
            id: newUser.id,
            // New fields
            systemID: newUser.systemID || '',
            facilityID: newUser.facilityID || '',
            facilityAccess: newUser.facilityAccess || '',
            facilityAccessType: facilityAccessType,
            additionalFacilities: additionalFacilities
          };

          // Emit subcounty change to load wards if needed
          if (newUser.subCountyID) {
            this.$emit('subcounty-change', newUser.subCountyID);
          }
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    getRight(type) {
      return getRight(type);
    },

    findSubCountyName(subCountyID) {
      const subCounty = this.subCounties.find(sc => sc.subCountyID === subCountyID);
      return subCounty ? subCounty.subCountyName : '';
    },

    findWardName(wardID) {
      const ward = this.wards.find(w => w.wardID === wardID);
      return ward ? ward.wardName : '';
    },

    parseZone(zoneString) {
      if (!zoneString) return [];
      return Array.isArray(zoneString) ? zoneString : [zoneString];
    },

    handleSubCountyChange() {
      this.formData.subCountyName = this.findSubCountyName(this.formData.subCountyID);
      this.$emit('subcounty-change', this.formData.subCountyID);
      this.formData.wardID = '';
      this.formData.wardName = '';
      this.formData.zone = [];
    },

    handleSystemChange() {
      // Reset category when system changes
      this.formData.category = '';

      // Reset facility fields if not hospital
      if (!this.isHospitalSystem) {
        this.formData.facilityID = '';
        this.formData.facilityAccess = '';
        this.formData.facilityAccessType = 'single';
        this.formData.additionalFacilities = [];
      }

      // Emit event for parent to load categories by system
      this.$emit('system-change', this.formData.systemID);
    },

    handleFacilityAccessTypeChange() {
      if (this.formData.facilityAccessType === 'all') {
        this.formData.facilityID = '';
        this.formData.additionalFacilities = [];
      } else if (this.formData.facilityAccessType === 'single') {
        this.formData.additionalFacilities = [];
      }
    },

    handleSave() {
      if (!this.isFormValid) {
        alert("Please fill all required fields");
        return;
      }

      // Update ward name before saving
      if (this.formData.wardID) {
        this.formData.wardName = this.findWardName(this.formData.wardID);
      }

      // Update subcounty name before saving
      if (this.formData.subCountyID) {
        this.formData.subCountyName = this.findSubCountyName(this.formData.subCountyID);
      }

      // Build facility access for hospital users
      let facilityAccess = '';
      if (this.isHospitalSystem) {
        if (this.formData.facilityAccessType === 'all') {
          facilityAccess = 'ALL';
        } else if (this.formData.facilityAccessType === 'multiple') {
          let allFacilities = [...this.formData.additionalFacilities];
          if (this.formData.facilityID && !allFacilities.includes(this.formData.facilityID)) {
            allFacilities.unshift(this.formData.facilityID);
          }
          facilityAccess = JSON.stringify(allFacilities);
        }
      }

      const userData = {
        ...this.formData,
        requireOTP: this.formData.requireOTP ? 'true' : 'false',
        facilityAccess: facilityAccess,
        zone: Array.isArray(this.formData.zone) ? this.formData.zone.join(',') : this.formData.zone
      };

      // Remove helper fields
      delete userData.facilityAccessType;
      delete userData.additionalFacilities;

      this.$emit('save', userData);
    }
  }
}
</script>

<style scoped>
.form-label {
  font-weight: 500;
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.form-switch .form-check-input {
  width: 3em;
  height: 1.5em;
}

.modal-dialog-scrollable .modal-body {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.border-bottom {
  border-bottom: 2px solid #e9ecef !important;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.badge {
  font-size: 0.7rem;
}

.alert-info {
  background-color: #e7f3ff;
  border-color: #b6d4fe;
  color: #084298;
}
</style>